import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import GreetingHeader from "../../components/dashboard/GreetingHeader";
import StatCard from "../../components/dashboard/StatCard";
import EmptyHint from "../../components/dashboard/EmptyHint";
import MonthlyReportButton from "../../components/dashboard/MonthlyReportButton";
import TerritoryLabel from "../../components/common/TerritoryLabel";
import { num, pctLabel, count, NO_VALUE, joinParts } from "../../lib/vocabulary";
import { formatISTDateTime } from "../../lib/datetime";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Users,
  UserCog,
  ClipboardList,
  CheckCircle2,
  FileBarChart,
  CalendarClock,
  Trophy,
  Activity,
  Inbox,
} from "lucide-react";

export default function DirectorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/director");
        if (!cancelled) setData(data);
      } catch (e) {
        // A failed fetch used to be indistinguishable from a quiet day: `data`
        // stayed null, every `?? 0` fired, and the page reported a confident
        // company-wide zero. The rejection was not even caught, so nothing on
        // screen or in the console said the request had failed.
        if (!cancelled) {
          setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const s = data?.stats || {};

  // `pctLabel` over the two counts, so a missing numerator or a plan of zero
  // yields the dash rather than "0% of plan" — the same rule the Assistant
  // Manager dashboard has carried since #37, now from the one implementation.
  const completion = pctLabel(s.today_visits_completed, s.today_visits_planned);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <GreetingHeader
        user={user}
        subtitle="Company-wide pulse. Drill into any tile to investigate further."
      />

      {/* Pattern A: the page says the numbers did not arrive rather than
          rendering zeroes that look like a quiet day. */}
      {error && (
        <div
          className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
          data-testid="dashboard-error"
        >
          Dashboard figures did not load — {error}
        </div>
      )}

      {/* The one-button monthly report — the same control on every dashboard. */}
      <MonthlyReportButton />

      {/* Every tile navigates. The subtitle promises a drill-down, and four of
          the six used to be inert (RC-15/F279): a card that lifts and shadows
          on hover but does nothing is a broken control, not a decoration. */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* No `?? s.active_sales_officers` fallback. That fallback rendered an
            OFFICERS-ONLY number under the helper "Officers + managers" the
            moment a backend stopped sending `active_field_staff` — a wrong
            label on a real number, which is worse than a dash. */}
        <StatCard
          testId="stat-active-sos"
          label="Field staff"
          value={num(loading, s.active_field_staff)}
          helper="Officers + Sales Mgrs"
          Icon={Users}
          onClick={() => navigate("/team")}
          loading={loading}
        />
        {/* "Managers", not "Sales Managers": the value counts assistant_manager
            AND admin_manager. D2 widened the count deliberately and the label
            was never updated, so this read 3 against two Sales Managers. The
            Team page has always distinguished them —
            "N Admin Mgr · N Sales Mgr" — which is what made the tile's 3
            look wrong rather than the Team page's 2. */}
        <StatCard
          testId="stat-active-ams"
          label="Managers"
          value={num(loading, s.active_assistant_managers)}
          helper="Sales + Admin"
          Icon={UserCog}
          onClick={() => navigate("/team")}
          loading={loading}
        />
        <StatCard
          testId="stat-visits-planned"
          label="Visits Planned"
          value={num(loading, s.today_visits_planned)}
          helper="Today, company-wide"
          Icon={ClipboardList}
          onClick={() => navigate("/route-planner")}
          loading={loading}
        />
        <StatCard
          testId="stat-visits-completed"
          label="Visits Completed"
          value={num(loading, s.today_visits_completed)}
          helper={completion === NO_VALUE ? "Today, company-wide" : `${completion} of plan`}
          Icon={CheckCircle2}
          accent
          onClick={() => navigate("/doctor-visits")}
          loading={loading}
        />
        {/* The rate is a fraction of 1, so the denominator IS 1 — `pctLabel`
            still refuses to render "0%" for a key the response did not carry.
            The helper names the denominator, because the figure is visits per
            head per CALENDAR day so far this month, not a submission count:
            two readers comparing it against the working-day attendance
            percentage were comparing different denominators. */}
        <StatCard
          testId="stat-dcr-rate"
          label="DCR Submission"
          value={pctLabel(s.monthly_dcr_rate, 1)}
          helper="Per head, per calendar day"
          Icon={FileBarChart}
          onClick={() => navigate("/analytics")}
          loading={loading}
        />
        {/* Replaces the leaves-only tile rather than sitting beside it. Two
            pending numbers on one dashboard, scoped differently, is the problem
            this was meant to solve restated. */}
        <StatCard
          testId="stat-approvals-pending"
          label="Approvals Pending"
          value={num(loading, s.approvals_pending_count)}
          helper="Expenses, leaves and tour plans"
          Icon={CalendarClock}
          onClick={() => navigate("/approvals")}
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Top field staff */}
        <Card className="lg:col-span-2 bg-white border-callisto shadow-sm rounded-xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-callisto-navy flex items-center gap-2">
              <Trophy className="h-4 w-4 text-callisto-gold" />
              Top field staff · This month
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : data?.top_sales_officers?.length ? (
              <ol className="space-y-1" data-testid="top-sales-officers">
                {data.top_sales_officers.map((u, idx) => (
                  <li
                    key={u.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-callisto-app"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-6 w-6 shrink-0 rounded-full bg-callisto-navy text-white text-[11px] font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-callisto-navy truncate">
                          {u.name}
                        </p>
                        <TerritoryLabel
                          value={u.territory}
                          unmigrated={u.territory_unmigrated}
                          review="icon"
                          className="text-[11px] text-callisto-muted uppercase tracking-wider truncate block"
                        />
                      </div>
                    </div>
                    <span className="text-sm font-bold text-callisto-navy whitespace-nowrap">
                      {count(u.visit_count)}{" "}
                      <span className="text-[10px] font-medium text-callisto-muted">visits</span>
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              // NOT "No sales officers yet". The list this sits under is
              // `top_sales_officers`, whose backend fallback selects FIELD_ROLES
              // — Sales Officers AND Sales Managers — so the empty state named a
              // population the populated state does not have (RC-9).
              <EmptyHint
                Icon={Users}
                title="No field staff yet"
                hint="Sales Officers and Sales Managers appear here once they are on the team and logging visits."
                cta={{ to: "/team", label: "Open Team" }}
                testId="top-field-staff-empty"
              />
            )}
          </CardContent>
        </Card>

        {/* Activity feed */}
        <Card className="lg:col-span-3 bg-white border-callisto shadow-sm rounded-xl">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base text-callisto-navy flex items-center gap-2">
              <Activity className="h-4 w-4 text-callisto-gold" />
              Recent activity
            </CardTitle>
            <span className="text-[11px] uppercase tracking-wider text-callisto-muted">
              Last 10 actions
            </span>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : data?.recent_activity?.length ? (
              <ul className="space-y-2" data-testid="recent-activity">
                {data.recent_activity.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/doctor-visits/${a.id}`)}
                      className="w-full text-left flex items-start gap-3 px-3 py-2 rounded-md hover:bg-callisto-app focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-callisto-gold"
                    >
                      <div className="h-2 w-2 rounded-full bg-callisto-gold mt-2" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-callisto-body">{a.message}</p>
                        {/* `submitted_at` is UTC. Rendered raw it read as a
                            different day for anything filed between 00:00 and
                            05:30 IST — RC-8, and the reason `formatISTDateTime`
                            names the zone. */}
                        <p className="text-[11px] text-callisto-muted">
                          {joinParts([a.actor, formatISTDateTime(a.timestamp)])}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyHint
                Icon={Inbox}
                title="Activity feed is empty"
                hint="As reps log visits, file expenses, and apply for leaves, the feed will populate here."
                testId="recent-activity-empty"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
